import { bus, type Events } from "../core/bus";

// FIRST FLIGHT: contextual gunner hints for a new career — each fires once
// per SESSION, triggered by the first occurrence of the thing it teaches
// (the missile hint arrives WITH the first missile, not in a manual).
// Enabled by the flow on early-career missions only; pure observer, never
// mutates game state, keeps no persistence (a session replay is fine —
// nobody re-reads a hint they've internalized, and the save stays clean).

type HintDef = {
  id: string;
  event: keyof Events | "start";
  text: string;
  delay?: number; // seconds after trigger before showing (start hints stagger)
};

const HINTS: readonly HintDef[] = [
  { id: "orbit", event: "start", text: "WASD steers the orbit. The guns follow your mouse." },
  { id: "weapons", event: "start", text: "[1] 25MM for infantry · [2] 30MM for vehicles · [3] 105MM for armor and buildings.", delay: 8 },
  { id: "contact", event: "enemy:fired", text: "Contact. Short bursts — watch the THERMAL LOAD bar on the left." },
  { id: "danger-close", event: "squad:underFire", text: "Alpha is in contact. Fire NEAR them, never ON them — blast has no IFF." },
  { id: "ricochet", event: "armor:ricochet", text: "Ricochet — the 25MM cannot hurt armor. Switch to [3] the 105MM." },
  { id: "flak", event: "flak:burst", text: "AA fire. Climb with [X] or kill the gun — LOW altitude is a raid, not a home." },
  { id: "missile", event: "flak:missileLaunch", text: "MISSILE INBOUND — [SPACE] FLARES. NOW." },
  { id: "hold", event: "squad:memberDown", text: "[H] stops Alpha in place. Halt them before pushing through a kill zone." },
];

export class HintTicker {
  // Session-wide dedupe: a hint internalized in mission one stays learned
  private static shown = new Set<string>();

  private readonly el: HTMLElement;
  private readonly offs: Array<() => void> = [];
  private readonly pending: Array<{ def: HintDef; at: number }> = [];
  private elapsed = 0;
  private visibleUntil = 0;

  constructor(enabled: boolean) {
    this.el = document.createElement("div");
    this.el.id = "hint-ticker";
    this.el.className = "hint-ticker";
    document.body.appendChild(this.el);
    if (!enabled) return;

    for (const def of HINTS) {
      if (HintTicker.shown.has(def.id)) continue;
      if (def.event === "start") {
        this.pending.push({ def, at: def.delay ?? 0 });
        continue;
      }
      const event = def.event as keyof Events;
      const handler = () => {
        this.trigger(def);
        off();
      };
      const off = (): void => bus.off(event, handler);
      bus.on(event, handler);
      this.offs.push(off);
    }
  }

  private trigger(def: HintDef): void {
    if (HintTicker.shown.has(def.id)) return;
    HintTicker.shown.add(def.id);
    this.el.innerHTML = `<span class="hint-ticker__tag">FIRST FLIGHT</span> ${def.text}`;
    this.el.classList.add("is-visible");
    this.visibleUntil = this.elapsed + 7;
  }

  update(dt: number): void {
    this.elapsed += dt;
    for (let i = this.pending.length - 1; i >= 0; i--) {
      if (this.elapsed >= this.pending[i]!.at) {
        this.trigger(this.pending[i]!.def);
        this.pending.splice(i, 1);
      }
    }
    if (this.visibleUntil > 0 && this.elapsed >= this.visibleUntil) {
      this.el.classList.remove("is-visible");
      this.visibleUntil = 0;
    }
  }

  dispose(): void {
    for (const off of this.offs) off();
    this.el.remove();
  }
}
